import {defineStore} from 'pinia'
import {matchaParams} from '@/scripts/matcha'
import Swal from 'sweetalert2'
import User from '../models/User'
import {AUTH_TOKEN_NAME, LAST_FULL_PATH, PREVIOUS_AUTH_TOKEN_NAME, ROUTES, SPECIAL_ROLES} from "@/scripts/constants";

export const useUserStore = defineStore({
    // name of the store
    // it is used in devtools and allows restoring state
    id: 'user',
    // a function that returns a fresh state
    state: () => ({
        user: typeof localStorage[AUTH_TOKEN_NAME] === "undefined" ? null : User.from(localStorage[AUTH_TOKEN_NAME]),
        users: [],
        created: false,
        itemLength: 0,
        loading: false,
        serverOptions: {
            page: 0,
            rows: 20,
            sortBy: 'age',
            sortType: 'desc',
        },
        tokenRefresh: false,
    }),
    // optional getters
    getters: {
        currentUser() {
            this.autoLoad()
            return this.users.find((u) => u.id === this.user.id)
        },
        sipUid() {
            return this.currentUser?.sip_uid
        },
        sipProvider() {
            return this.currentUser?.sip_provider
        },
    },
    // optional actions
    actions: {
        createUser(token) {
            localStorage.tantum_auth_token = token
            this.user = User.from(token)
        },
        login(provider, email, password, matchaToken = '') {
            return this.apiClient.post(`/auth/login`, {email, password, ...matchaParams(matchaToken)}, {
                suppressGlobalErrorPresentation: true,
                skipUnauthorizedLogout: true,
            })
        },
        logout({preservePath = false} = {}) {
            if (localStorage.getItem(PREVIOUS_AUTH_TOKEN_NAME)) {
                this.loginSuccessful(localStorage.getItem(PREVIOUS_AUTH_TOKEN_NAME))
                localStorage.removeItem(PREVIOUS_AUTH_TOKEN_NAME)
            } else {
                const currentRoute = this.$router?.currentRoute?.value
                const publicRoutes = [ROUTES.LOGIN, ROUTES.REGISTER, ROUTES.CONFIRM]
                if (preservePath && this.user && currentRoute && !publicRoutes.includes(currentRoute.name)) {
                    sessionStorage.setItem(LAST_FULL_PATH, currentRoute.fullPath)
                } else {
                    sessionStorage.removeItem(LAST_FULL_PATH)
                }
                localStorage.removeItem(AUTH_TOKEN_NAME)
                this.user = null
            }
            // this.$router.push({name: ROUTES.LOGIN})
        },
        restorePath() {
            const lastFullPath = sessionStorage.getItem(LAST_FULL_PATH)
            if (lastFullPath) {
                this.$router.replace(lastFullPath)
                sessionStorage.removeItem(LAST_FULL_PATH)
                return true
            }
            return null
        },
        loginSuccessful(token) {
            if (!token) {
                return false
            }
            this.createUser(token)
            if (!this.user) {
                return false
            }
            if (!this.restorePath()) {
                if (this.user.specialRole && this.user.specialRole === SPECIAL_ROLES.taxOffice) {
                    this.$router.push({name: ROUTES.ACCOUNTS.INDEX})
                } else {
                    this.$router.push({name: ROUTES.DASHBOARD})
                }
            }
            return true
        },
        register(userData) {
            return this.apiClient.post(`/users`, {user: userData}).then(({data}) => {
                if (data.error) {
                    Swal.fire({
                        title: 'Error!',
                        text: JSON.stringify(data.message),
                        icon: 'error',
                        confirmButtonText: 'Ok'
                    })
                } else {
                    this.created = true;
                }
            })
        },
        autoRefresh() {
            if (this.user?.expiresSoon()) {
                return this.refreshAccessToken()
            }
        },
        refreshAccessToken() {
            if (this.tokenRefresh) {
                return
            }
            if (!this.user) {
                console.log("no user session available => skipping refresh")
                return
            }
            this.tokenRefresh = true
            return this.apiClient.put(`/auth`, {}, {
                suppressGlobalErrorPresentation: true,
            }).then(({data}) => {
                this.createUser(data.token)
            }).catch(() => undefined).finally(() => this.tokenRefresh = false)
        },
        confirm(uuid) {
            return this.apiClient.get(`/account_confirmation/${uuid}`);
        },
        index() {
            this.loading = true
            return this.apiClient.get(`/users`, {
                params: {
                    page: this.serverOptions.page + 1,
                    size: this.serverOptions.rows
                }
            }).then(({data}) => {
                this.users = data.users
                this.itemLength = data.itemLength
                this.loading = false
            })
        },
        autoLoad() {
            if (this.users.length || this.loading) {
                return
            }
            this.index()
        },
        show(id) {
            return this.apiClient.get(`/users/${id}`)
        },
        update(user, id, permissions) {
            return this.apiClient.put(`/users/${id}`, {user, permissions})
        },
        impersonate(id) {
            return this.apiClient.get(`/users/${id}/impersonate`)
        },
        triggerJobs() {
            return this.apiClient.post(`/product_billing_periods/trigger_jobs`, {})
        },
        call(number) {
            return this.apiClient.post(`/calls`, {target: number})
        },
    },
})
